import HeaderElement from "../../components/HeaderElement/HeaderElement";
import SearchBar from "../../components/MemberManagement/SearchBar/SearchBar";
import HistoryVoteCard from "../../components/Voting/HistoryVoteCard/HistoryVoteCard";
import VoteButton from "../../components/Buttons/VoteButton/VoteButton";
import PopUpResponseButton from "../../components/Buttons/PopUpResponseButton/PopUpResponseButton";
import { useApi } from "../../hooks/useApi";
import strings from "../../locales/ro.json"
import { UserAuth } from "../../context/AuthContext";
import LoadingCircle from "../../components/Global/LoadingCircle";
import { Modal } from "@mui/material";

import React, { useMemo } from "react";

import "./VoteHistory.css";

import { Tooltip } from "react-tooltip";

const VoteHistory = () => {
  const { user } = UserAuth();
  const { api } = useApi();
  const [ballots, setBallots] = React.useState([]);
  const [filteredBallots, setFilteredBallots] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showVotersModal, setShowVotersModal] = React.useState(false);
  const [votersData, setVotersData] = React.useState({
    day: "",
    ballots: [],
    voters: [],
    total_voters: 0,
    total_ballots: 0
  });
  const [isLoadingVoters, setIsLoadingVoters] = React.useState(false);
  const [copyText, setCopyText] = React.useState("Copiază lista");

  // Group ballots by creation date
  const groupedBallots = useMemo(() => {
    const groups = {};

    filteredBallots.forEach(ballot => {
      const createdDate = new Date(ballot.created_at);
      const dateKey = createdDate.toDateString();

      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: createdDate,
          ballots: []
        };
      }
      groups[dateKey].ballots.push(ballot);
    });

    // Sort groups by date (newest first)
    return Object.values(groups).sort((a, b) => b.date - a.date);
  }, [filteredBallots]);

  const processBallotData = (data) => {
    // Filter only closed ballots, sort by created_at date (newest first), and limit to 50
    const closed = data
      .filter(ballot => ballot.closed_at !== null)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 50);

    setBallots(closed);
    setFilteredBallots(closed);
    setIsLoading(false);
  };

  React.useEffect(() => {
    if (user) {
      const fetchBallots = async () => {
        try {
          if (user.accessToken === undefined)
            return;

          const result = await api("ballot/history", "GET");
          console.log("Result", result);
          processBallotData(result);
        } catch (error) {
          console.error("Error fetching ballots:", error);
          setIsLoading(false);
        }
      };
      fetchBallots();
    }
  }, [user]);

  const handleFilterChange = (event) => {
    const searchTerm = event.target.value.toLowerCase();
    const filtered = ballots.filter((element) => {
      return element.title.toLowerCase().includes(searchTerm);
    });
    setFilteredBallots(filtered);
  };

  // Format date for display
  const formatDate = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Astăzi";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Ieri";
    } else {
      return date.toLocaleDateString('ro-RO', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  // Format date for API request (DD.MM.YYYY)
  const formatDateForAPI = (date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Fetch voters for a specific day
  const fetchVotersForDay = async (date) => {
    try {
      // Open modal immediately with loading state
      setShowVotersModal(true);
      setIsLoadingVoters(true);

      const dayString = formatDateForAPI(date);

      const result = await api(
        "ballot/voters-by-day",
        "POST",
        { day: dayString }
      );

      setVotersData(result);
    } catch (error) {
      console.error("Error fetching voters:", error);
    } finally {
      setIsLoadingVoters(false);
    }
  };

  if (isLoading) {
    return (
      <div className="voteHistoryPageWrapper">
        <div className="voteHistoryGrid">
          <HeaderElement headerText={strings.voteHistory.title} />
          <LoadingCircle />
        </div>
      </div>
    );
  }

  return (
    <div className="voteHistoryPageWrapper">
      <div className="voteHistoryGrid">
        <HeaderElement headerText={strings.voteHistory.title} />
        <div className="historySearch">
          <SearchBar handleChange={handleFilterChange} />
        </div>
        <div className="voteHistoryContent">
          {groupedBallots.length > 0 ? (
            groupedBallots.map((group, groupIndex) => (
              <div key={group.date.toISOString()} className="dateGroup">
                <div className="dateGroupHeader">
                  <div className="dateGroupTitle">
                    <h2 className="dateTitle">{formatDate(group.date)}</h2>
                  </div>
                  <VoteButton
                    text="Vezi Votanți"
                    isPressed={false}
                    onClick={() => fetchVotersForDay(group.date)}
                    style={{ width: 'auto', padding: '12px 20px' }}
                  />
                </div>
                <div className="voteCardWrapper">
                  {group.ballots.map((ballot) => (
                    <HistoryVoteCard
                      key={ballot.id}
                      title={ballot.title}
                      description={ballot?.question_text || ""}
                      ballotId={ballot.id}
                      closedAt={ballot.closed_at}
                      voters={ballot.inherit_voters || 0}
                      ballotData={ballot}
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="no-ballots-text">
              <p>Nu există voturi anterioare</p>
            </div>
          )}
        </div>
      </div>

      {/* Voters Modal */}
      <Modal open={showVotersModal} onClose={() => setShowVotersModal(false)}>
        <div className="voteStatusModalContainer">
          <div className="voteStatusModalVoters">
            <div className="voteStatusModalHeader">Votanți pentru {votersData.day}</div>
            <div className="voteStatusModalCount">
              {votersData.total_voters} votanți
            </div>

            {isLoadingVoters ? (
              <div className="loadingContainer">
                <LoadingCircle />
              </div>
            ) : votersData.voters && votersData.voters.length > 0 ? (
              <div className="voteStatusModalVotersList">
                {votersData.voters.map((voter, index) => (
                  <div key={`voter-${index}`} className="voterItem">
                    <div className="voterName">{voter.name}</div>
                    <div className="voterEmail">{voter.email}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="voteStatusModalVoters">
                <div className="voteStatusModalHeader">Nu există votanți pentru această zi</div>
              </div>
            )}
          </div>

          <div className="voteStatusModalButton" style={{ gap: '10px' }}>
            <PopUpResponseButton
              type="cancel"
              text="Copiază lista"
              data-tooltip-id="copyVotersTooltip"
              data-tooltip-content="Copiat!"
              onClick={() => {
                const textToCopy = votersData.voters.map(v => `${v.name} - ${v.email}`).join('\n');
                navigator.clipboard.writeText(textToCopy);
              }}
            />
            <PopUpResponseButton
              type="cancel"
              text="Închide"
              onClick={() => setShowVotersModal(false)}
            />
          </div>
          <Tooltip
            id="copyVotersTooltip"
            events={['click']}
            delayHide={2000}
            place="top"
          />
        </div>
      </Modal>
    </div>
  );
};

export default VoteHistory;
